import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { UploadIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { z } from 'zod';
import type { DocState, DocumentBook } from '@worldathletics/types';
import type { components } from '@worldathletics/api-client';
import {
  PageContentLayout,
  PageContentLayoutHeader,
  PageContentLayoutMain,
  PageContentLayoutSubheader,
} from '@/components/layout';
import {
  Button,
  Filters,
  Pagination,
  type DropdownOptionProps,
} from '@/components/atoms';
import { CustomTag, CustomTitle } from '@/components/typography';
import { Dropdown } from '@/components/atoms';
import { TableComponent, type TableColumnDef } from '@/components/table';
import { ModalComponent } from '@/components/modal';
import {
  CreateBookModal,
  DeleteBookModal,
  UploadFileDocument,
} from '@/components/modals';
import {
  Field,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui';
import { PermissionGate } from '@/context/auth/permission-gate';
import { usePermission } from '@/hooks/usePermission';
import { Permission } from '@worldathletics/utilities';
import { StatusToDisplayLabel } from '@/lib/consts';
import { useDocumentsQuery } from '@/query/book';
import { DOCUMENT_STATUSES } from '@/lib/site-config';
import '@/styles/route-documents.scss';

type DocumentBookListItem = components['schemas']['DocumentBookResponseDto'];

const StateSortOrderByDisplayLabel = Object.entries(StatusToDisplayLabel)
  .sort(([, labelA], [, labelB]) => labelA.localeCompare(labelB))
  .map(([state]) => state) as DocState[];

const documentsSearchSchema = z.object({
  state: z
    .enum(['DRAFT', 'REVIEW', 'SCHEDULED', 'PUBLISHED', 'ARCHIVED', 'DELETED'])
    .optional(),
  sortStateOrder: z
    .array(
      z.enum([
        'DRAFT',
        'REVIEW',
        'SCHEDULED',
        'PUBLISHED',
        'ARCHIVED',
        'DELETED',
      ]),
    )
    .optional(),
  search: z.string().optional(),
  recent: z.coerce.boolean().optional(),
  lostLinks: z.coerce.boolean().optional(),
  published: z.coerce.boolean().optional(),
  drafts: z.coerce.boolean().optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(10),
  sort: z
    .enum([
      'createdAt',
      'updatedAt',
      'name',
      'dateModified',
      'datePublished',
      'state',
      'versions',
      'references',
    ])
    .default('name'),
  order: z.enum(['asc', 'desc']).default('asc'),
});

export const Route = createFileRoute('/_auth/documents')({
  validateSearch: documentsSearchSchema,
  component: DocumentsPage,
});

type DocumentsSearch = z.infer<typeof documentsSearchSchema>;

function DocumentsPage() {
  const params = Route.useSearch();
  const navigate = useNavigate();

  const [isUploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const [isCreateModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [pendingDelete, setPendingDelete] = useState<DocumentBook | null>(null);

  const [searchInput, setSearchInput] = useState<string>(params.search ?? '');

  useEffect(() => {
    const current = params.search ?? '';
    if (searchInput === current) return;
    const trimmed = searchInput.trim();
    const handler = setTimeout(() => {
      void navigate({
        to: '/documents',
        search: prev => ({
          ...(prev as DocumentsSearch),
          search: trimmed.length > 0 ? trimmed : undefined,
          page: 1,
        }),
      });
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput, params.search, navigate]);

  const canDelete = usePermission(Permission.DocumentsDelete);
  const canRestore = usePermission(Permission.DocumentsRestore);
  const canReadDeletedBooks = canDelete || canRestore;

  useEffect(() => {
    if (params.state === 'DELETED' && !canReadDeletedBooks) {
      void navigate({
        to: '/documents',
        search: prev => ({
          ...(prev as DocumentsSearch),
          state: undefined,
          page: 1,
        }),
      });
    }
  }, [params.state, canReadDeletedBooks, navigate]);

  const listQueryStates = useMemo((): DocState[] => {
    const out: DocState[] = [];
    if (params.state !== undefined) {
      out.push(params.state);
    }
    if (params.published) {
      out.push('PUBLISHED');
    }
    if (params.drafts) {
      out.push('DRAFT');
    }
    return [...new Set(out)];
  }, [params.state, params.published, params.drafts]);

  const documentsQuery = useDocumentsQuery({
    ...(listQueryStates.length > 0 ? { states: listQueryStates } : {}),
    search: params.search,
    recent: params.recent,
    ...(params.lostLinks ? { lostLinks: true } : {}),
    page: params.page,
    limit: params.limit,
    sort: params.sort,
    order: params.order,
    ...(params.sort === 'state'
      ? { sortStateOrder: StateSortOrderByDisplayLabel }
      : {}),
  });

  const statusOptions: DropdownOptionProps[] = useMemo(() => {
    const opts: DropdownOptionProps[] = [{ value: '', label: 'All documents' }];
    const visibleStatuses = DOCUMENT_STATUSES.filter(
      status => status !== 'DELETED' || canReadDeletedBooks,
    );
    const extra = visibleStatuses.map(status => ({
      value: status,
      label: StatusToDisplayLabel[status],
    }));
    opts.push(...extra);
    return opts;
  }, [canReadDeletedBooks]);

  const items: DocumentBookListItem[] = documentsQuery.data?.data ?? [];
  // Cast for TableComponent which currently types data as DocumentBook[] (the
  // shared shape); the BE response is structurally compatible for the columns
  // we render.
  const tableData = items as unknown as DocumentBook[];
  const meta = documentsQuery.data?.meta;
  const docNum = meta?.total ?? items.length;
  const currentPage = meta?.page ?? params.page;
  const totalPages = meta?.totalPages ?? 1;

  const columnNames: TableColumnDef[] = [
    { displayName: 'Documents', id: 'name', sortKey: 'name' },
    { displayName: 'State', id: 'state', sortKey: 'state' },
    { displayName: 'Last edit', id: 'dateModified', sortKey: 'dateModified' },
    {
      displayName: 'Date published',
      id: 'datePublished',
      sortKey: 'datePublished',
    },
    { displayName: 'Version', id: 'versions', sortKey: 'versions' },
    { displayName: 'References', id: 'references', sortKey: 'references' },
  ];

  const handleStateChange = (option: DropdownOptionProps) => {
    const value = option.value ? option.value : undefined;
    void navigate({
      to: '/documents',
      search: prev => ({
        ...(prev as DocumentsSearch),
        state: value as DocumentsSearch['state'],
        page: 1,
      }),
    });
  };

  const handlePrevious = () => {
    if (currentPage <= 1) return;
    void navigate({
      to: '/documents',
      search: prev => ({
        ...(prev as DocumentsSearch),
        page: currentPage - 1,
      }),
    });
  };

  const handleNext = () => {
    if (currentPage >= totalPages) return;
    void navigate({
      to: '/documents',
      search: prev => ({
        ...(prev as DocumentsSearch),
        page: currentPage + 1,
      }),
    });
  };

  const canEditBook = usePermission(Permission.DocumentsEdit);

  const isLoading = documentsQuery.isLoading;
  const isError = documentsQuery.isError;

  return (
    <PageContentLayout>
      <PageContentLayoutHeader
        buttons={
          <>
            <PermissionGate permission={Permission.DocumentsCreate}>
              <Button
                variant="default"
                type="button"
                onClick={() => setUploadModalOpen(true)}
              >
                Upload book <UploadIcon size={16} />
              </Button>
            </PermissionGate>
            <PermissionGate permission={Permission.DocumentsCreate}>
              <Button
                variant="default"
                type="button"
                onClick={() => setCreateModalOpen(true)}
              >
                Create book <PlusIcon size={16} />
              </Button>
            </PermissionGate>
          </>
        }
      >
        <CustomTitle level="h1">
          My documents <CustomTag>{docNum}</CustomTag>
        </CustomTitle>
      </PageContentLayoutHeader>

      <PageContentLayoutSubheader className="documents-subheader">
        <Dropdown
          options={statusOptions}
          label="Show:"
          onChange={handleStateChange}
          defaultValue={params.state ?? ''}
        />
        <Filters
          label="Filter by:"
          params={{
            recent: params.recent,
            lostLinks: params.lostLinks,
            published: params.published,
            drafts: params.drafts,
          }}
        />
        <div className="search">
          <Field className="search-field">
            <InputGroup>
              <InputGroupAddon align="inline-start">
                <SearchIcon className="search-icon" color="#1E1E1E" />
              </InputGroupAddon>
              <InputGroupInput
                id="documents-search-input"
                placeholder="Search..."
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
              />
            </InputGroup>
          </Field>
        </div>
      </PageContentLayoutSubheader>

      <PageContentLayoutMain>
        {isError ? (
          <div className="documents-error">
            <p>Failed to load documents.</p>
            <Button
              type="button"
              variant="default"
              onClick={() => void documentsQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : !isLoading && items.length === 0 ? (
          <div className="documents-empty">
            <p>No documents yet.</p>
          </div>
        ) : (
          <TableComponent
            data={tableData}
            columnNames={columnNames}
            isLoading={isLoading}
            canEditBook={canEditBook}
            onDelete={canDelete ? book => setPendingDelete(book) : undefined}
            serverSort={{ column: params.sort, direction: params.order }}
            onServerSortChange={(column, direction) => {
              void navigate({
                to: '/documents',
                search: prev => ({
                  ...(prev as DocumentsSearch),
                  sort: column as DocumentsSearch['sort'],
                  order: direction,
                  sortStateOrder:
                    column === 'state'
                      ? StateSortOrderByDisplayLabel
                      : undefined,
                  page: 1,
                }),
              });
            }}
          />
        )}
      </PageContentLayoutMain>

      {!isError && items.length > 0 ? (
        <Pagination
          className="documents-pagination"
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPageCall={handlePrevious}
          onNextPageCall={handleNext}
        />
      ) : null}

      <ModalComponent
        isOpen={isUploadModalOpen}
        setIsOpen={setUploadModalOpen}
        body={<UploadFileDocument setIsOpen={setUploadModalOpen} />}
        title="Upload"
      />

      <ModalComponent
        isOpen={isCreateModalOpen}
        setIsOpen={setCreateModalOpen}
        body={
          <CreateBookModal
            isOpen={isCreateModalOpen}
            setIsOpen={setCreateModalOpen}
          />
        }
        title="Create book"
      />

      <ModalComponent
        isOpen={pendingDelete !== null}
        setIsOpen={open => {
          if (!open) setPendingDelete(null);
        }}
        body={
          pendingDelete ? (
            <DeleteBookModal
              isOpen={pendingDelete !== null}
              setIsOpen={open => {
                if (!open) setPendingDelete(null);
              }}
              bookId={pendingDelete.id}
              bookName={pendingDelete.name}
            />
          ) : null
        }
        title="Delete document"
      />
    </PageContentLayout>
  );
}
